import React, {useRef} from 'react';
import {Editor, Transforms} from 'slate';
import {useSlateStatic, ReactEditor} from 'slate-react';

import useDocumentStore from '../../../store/reportStore/documentStore';
import {CustomElement} from '../constants/ReportBuilderInterfce';

export const PageContext = React.createContext<number>(1);

interface PageRendererProps {
  attributes: any;
  children: React.ReactNode;
  element: CustomElement;
  watermark?: {
    text: string;
    color: string;
    fontSize: number;
    opacity: number;
    layout: 'diagonal' | 'horizontal';
    fontFamily: string;
  };
  pageMargins?: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  pageOrientation?: 'portrait' | 'landscape';
}

export const PageRenderer = ({
  attributes,
  children,
  element,
  watermark: propWatermark,
  pageMargins: propMargins,
  pageOrientation: propPageOrientation
}: PageRendererProps): React.JSX.Element => {
  const editor = useSlateStatic();

  const storeWatermark = useDocumentStore((s) => s.watermark);
  const storePageMargins = useDocumentStore((s) => s.pageMargins);
  const storePageOrientation = useDocumentStore((s) => s.pageOrientation);

  const watermark = propWatermark ?? storeWatermark;
  const pageMargins = propMargins ?? storePageMargins;
  const pageOrientation = propPageOrientation ?? storePageOrientation;
  const isLandscape = pageOrientation === 'landscape';
  const size = {
    width: isLandscape ? 11.69 : 8.27,
    height: isLandscape ? 8.27 : 11.69
  };

  const pW = Math.round(size.width * 96);
  const pH = Math.round(size.height * 96);
  const pT = (pageMargins?.top ?? 0.5) * 96;
  const pR = (pageMargins?.right ?? 0.5) * 96;
  const pB = (pageMargins?.bottom ?? 0.5) * 96;
  const pL = (pageMargins?.left ?? 0.5) * 96;
  const contentRef = useRef<HTMLDivElement>(null);

  const onPageMouseDown = (e: React.MouseEvent) => {
    // Only handle left clicks
    if (e.button !== 0) return;

    // Identify if the click is in the bottom "empty" section of the page.
    // We use the bounding box of the content container as our reference.
    if (contentRef.current) {
      const rect = contentRef.current.getBoundingClientRect();
      // If the click is above the bottom of the content, it's likely a click
      // on an actual node or margin handled by Slate.
      if (e.clientY <= rect.bottom) return;
    }

    try {
      const path = ReactEditor.findPath(editor as any, element);
      const pageNode = element;

      // Find the last editable block (not header/footer/page-break) on this page
      let lastContentPath = null;
      for (let i = pageNode.children.length - 1; i >= 0; i--) {
        const child = pageNode.children[i];
        const childType = (child as any).type;
        if (
          !['header-wrapper', 'footer-wrapper', 'page-break'].includes(
            childType as string
          )
        ) {
          lastContentPath = [...path, i];
          break;
        }
      }

      if (lastContentPath) {
        const end = Editor.end(editor, lastContentPath);
        Transforms.select(editor, end);
        // Ensure browser focus is on the editor
        setTimeout(() => {
          ReactEditor.focus(editor);
        }, 0);

        // Prevent default to avoid browser selection behavior interference
        e.preventDefault();
      }
    } catch (err) {
      // Ignore errors in finding path (might happen during mid-reflow)
    }
  };

  return (
    <div
      {...attributes}
      className="paginated-page"
      onMouseDown={onPageMouseDown}
      style={{
        width: element.width
          ? typeof element.width === 'number'
            ? `${element.width}px`
            : element.width
          : `${pW}px`,
        height: element.height
          ? typeof element.height === 'number'
            ? `${element.height}px`
            : element.height
          : `${pH}px`,
        padding: `${pT}px ${pR}px ${pB}px ${pL}px`,
        backgroundColor: 'white',
        boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
        margin: '20px auto',
        position: 'relative',
        boxSizing: 'border-box',
        overflow: 'visible'
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          pointerEvents: 'none',
          zIndex: 0
        }}
      >
        {watermark?.text && (
          <div
            className="watermark-overlay"
            contentEditable={false}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: `translate(-50%, -50%) ${
                watermark.layout === 'diagonal' ? 'rotate(-45deg)' : ''
              }`,
              fontSize: `${watermark.fontSize}px`,
              fontFamily: watermark.fontFamily,
              color: watermark.color,
              opacity: watermark.opacity,
              whiteSpace: 'nowrap',
              userSelect: 'none'
            }}
          >
            {watermark.text}
          </div>
        )}
      </div>
      <PageContext.Provider value={(element as any).pageNumber || 1}>
        <div ref={contentRef} style={{position: 'static', zIndex: 1}}>
          {children}
        </div>
      </PageContext.Provider>
    </div>
  );
};
